import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Card, Btn, DataTable } from "@/components/shared";

const ROLE_TABS = [["retailer", "Retailers"], ["sr", "Sales Reps"], ["distributor", "Distributors"], ["super", "Super Distributors"]];
const FIELDS = [["min", "Min ₹ per txn"], ["max", "Max ₹ per txn"], ["daily", "Daily max ₹"]];

function Tabs({ value, onChange, prefix }) {
  return (
    <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
      {ROLE_TABS.map(([v, l]) => (
        <button key={v} onClick={() => onChange(v)} className={`rounded-md px-3 py-1.5 text-xs font-semibold ${value === v ? "bg-slate-900 text-white" : "text-slate-600"}`} data-testid={`${prefix}-${v}`}>{l}</button>
      ))}
    </div>
  );
}

function LimitsEditor({ services, initial, onSave, prefix, extra }) {
  const [l, setL] = useState(initial);
  useEffect(() => setL(initial), [initial]);
  const set = (svc, k) => (e) => setL({ ...l, [svc]: { ...l[svc], [k]: e.target.value === "" ? "" : Number(e.target.value) } });
  return (
    <div data-testid={`${prefix}-editor`}>
      <table className="w-full text-left text-sm">
        <thead><tr className="text-xs text-slate-500"><th className="py-2">Service</th>{FIELDS.map(([, h]) => <th key={h} className="px-2">{h}</th>)}</tr></thead>
        <tbody>
          {Object.entries(services).map(([svc, label]) => (
            <tr key={svc} className="border-t border-slate-100">
              <td className="py-2 font-medium">{label}</td>
              {FIELDS.map(([k]) => (
                <td key={k} className="px-1 py-1.5"><input type="number" min="0" className="np-input py-1.5 font-mono text-xs" value={l[svc]?.[k] ?? 0} onChange={set(svc, k)} data-testid={`${prefix}-${svc}-${k}`} /></td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-2 text-[11px] text-slate-500">Move to Bank: default ₹1,001 – ₹50,000 per transfer; allowed range ₹1,001 – ₹1,00,000. Daily / Fund Request: 0 means no limit.</div>
      <div className="mt-4 flex gap-2">{extra}<Btn onClick={() => onSave(Object.fromEntries(Object.entries(l).map(([k, v]) => [k, Object.fromEntries(Object.entries(v).map(([f, n]) => [f, Number(n) || 0]))])))} data-testid={`${prefix}-save`}>Save limits</Btn></div>
    </div>
  );
}

function UserLimitsDialog({ user, services, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); if (user) api.get(`/admin/limits/user/${user.id}`).then(({ data }) => setD(data)); }, [user]);
  const put = async (limits, msg) => {
    try { const { data } = await api.put(`/admin/limits/user/${user.id}`, { limits }); setD(data); toast.success(msg); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="user-limits-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display">Limits · {user?.name} ({user?.login_id})</DialogTitle></DialogHeader>
        {!d ? <div className="text-sm text-slate-400">Loading…</div> : (
          <>
            <div className="text-xs font-semibold" data-testid="user-limits-source">{d.override ? <span className="text-blue-700">Custom limits for this user</span> : <span className="text-slate-500">Using role limits — edit and save to set custom limits</span>}</div>
            <LimitsEditor services={services} initial={d.override ? { ...d.role_limits, ...d.override } : d.role_limits} prefix="user-lim" onSave={(l) => put(l, "Custom limits saved")}
              extra={d.override && <Btn variant="outline" onClick={() => put(null, "Back to role limits")} data-testid="user-lim-reset">Use role limits</Btn>} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function AdminLimits() {
  const [cfg, setCfg] = useState(null);
  const [role, setRole] = useState("retailer");
  const [urole, setUrole] = useState("retailer");
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const loadUsers = () => api.get("/admin/users", { params: { role: urole } }).then(({ data }) => setUsers(data));
  useEffect(() => { api.get("/admin/limits").then(({ data }) => setCfg(data)); }, []);
  useEffect(() => { loadUsers(); }, [urole]); // eslint-disable-line
  const saveRole = async (limits) => {
    try { const { data } = await api.put(`/admin/limits/role/${role}`, { limits }); setCfg({ ...cfg, roles: { ...cfg.roles, [role]: data.limits } }); toast.success("Role limits saved"); } catch (e) { toast.error(errMsg(e)); }
  };
  const shown = users.filter((u) => !q || `${u.login_id} ${u.name} ${u.mobile}`.toLowerCase().includes(q.toLowerCase()));
  if (!cfg) return <div className="text-sm text-slate-400">Loading…</div>;
  return (
    <div>
      <PageHeader title="Transaction Limits" subtitle="Set minimum / maximum amount per transaction and a daily limit — for a whole role, or a single user." />
      <Card title="Role-wise limits" action={<Tabs value={role} onChange={setRole} prefix="lim-role" />}>
        <LimitsEditor key={role} services={cfg.services} initial={cfg.roles[role]} prefix="role-lim" onSave={saveRole} />
      </Card>
      <Card className="mt-6" title="User-wise limits" action={<Tabs value={urole} onChange={setUrole} prefix="lim-user-role" />}>
        <input className="np-input mb-3 max-w-sm" placeholder="Search login ID, name or mobile" value={q} onChange={(e) => setQ(e.target.value)} data-testid="lim-user-search" />
        <DataTable testid="lim-users-table" rows={shown} columns={[
          { label: "Login ID", render: (r) => <span className="font-mono text-xs">{r.login_id}</span> }, { label: "Name", key: "name" }, { label: "Mobile", key: "mobile" },
          { label: "Limits", render: (r) => (r.limits ? <span className="text-xs font-semibold text-blue-700">Custom</span> : <span className="text-xs text-slate-400">Role limits</span>) },
          { label: "Action", render: (r) => <button className="text-xs font-semibold text-blue-700" onClick={() => setSel(r)} data-testid={`manage-limits-${r.login_id}`}>Set limits</button> },
        ]} />
      </Card>
      <UserLimitsDialog user={sel} services={cfg.services} onClose={() => { setSel(null); loadUsers(); }} />
    </div>
  );
}
